import { useCallback, useEffect, useState } from "react";

import SingleSelect from "../FlowComponents/SingleSelect";
import Text from "../FlowComponents/Text";
import MultiSelect from "../FlowComponents/MultiSelect";
import Rating from "../FlowComponents/Rating";
import Range from "../FlowComponents/Range";
import { Image } from "../FlowComponents/Image";
import { Video } from "../FlowComponents/Video";
import { Document } from "../FlowComponents/Document";
import { Audio } from "../FlowComponents/Audio";
import Contact from "../FlowComponents/Contact";
import Typing from "../Typing";
import { BotIcon } from "../FlowComponents/BotIcon";
import PushNotifications from "../FlowComponents/PushNotifications";
import { useSelector } from "react-redux";
import { renderQuestion } from "../../utilities/RenderQuestion";
import SystemMessage from "../FlowComponents/SystemMessage";
import MessagedBy from "../FlowComponents/MessageBy";
import { back, skip } from "../../utilities/Response";
import moment from "moment";
import { updateEnvironment } from "../../actions/environment.action";
import { STORE } from "../../store";
import { scroll, adjustHexColor } from "../../services/shared";
import { Iframe } from "../FlowComponents/Iframe";
import { IframeLabel } from "../FlowComponents/IframeLabel";
import ParentChatMessage from "../ParentChatMessage";
import MessageActions from "../MessageActions";
import AiSuggestion from "../FlowComponents/AiSuggestion";
import { Element } from "react-scroll";
import Ai from "../FlowComponents/Ai";
import ListeningDots from "../ListeningDots";

const textCompOutput = ['statement', 'question', 'email', 'phone', 'file', 'appointment', 'location', 'date',
  'number', 'smart_question', 'name', 'live_chat'];
const nonAvatarComponents = ['statement', 'video', 'image', 'contact', 'system'];
const liveChatComp = ['live_chat_notification', 'live_chat'];
const mediaOptions = ['image', 'video', 'audio', 'document'];

export default function ChatBlock(props: { shouldFetchData: boolean }) {
  const environment = useSelector((state: any) => state?.environment);
  const flows = useSelector((state: any) => state?.flows);
  const messages = useSelector((state: any) => state?.messages);
  const design = useSelector((state: any) => state?.design);
  const [checkShouldRender, setCheckRender] = useState(true);
  const [messageActionIndex, setMessageActionIndex] = useState('');

  const haveSuggestions = () => {
    return !!environment?.activeQuestion?.suggestions?.length;
  }

  /**
   * Author: Divyansh
   * Date: 2024-27-06
   * Summary: Method to check if we should render message
   * Description: A bot message is not rendered if that last message was sent by bot within last 1 hour window
   */
  const shouldRenderMessage = useCallback(() => {

    const lastMessage = messages[messages.length - 1];
    const { createdAt, isErrorMessage, position: lastMessagePosition, type: lastMessageType } = lastMessage || {};

    let myTabCount = parseInt(sessionStorage.getItem('myTabCount') || '0');
    const isLiveChatActive =
      environment?.activeQuestionType === 'live_chat' || environment?.activeQuestionType === 'STATEMENT';

    /**
     *  - This method will be checked once on first initialisation of the app so we check it via checkShouldRender and
     *  - myTabCount is used to fix duplicate messages on initialisation issue if two or more browser tabs are opened parallely. 
     *    We are maintaining tab counts and only first tab is allowed to call renderQuestion method to start chat flow
     *  */

    if (!checkShouldRender || !!myTabCount) {
      if (!!lastMessage && lastMessage.label) {
        scroll(lastMessage?.id + ' ' + (lastMessage.label || '').slice(-10));
      } else {
        scroll();
      }

      return false;
    }

    /**
     * Chat is restarted on window reload only if:
     * 1. Last message was a bot error message (Done because no input box comes up as we do not know the last active question on reload)
     * 2. Last message was by the end user
     * 3. Last message was by bot and was sent 1 hour back 
     * 4. Last message was by bot and was of type statement (Done because no active input box comes up when question is type of statement)
     */
    if (
      isErrorMessage ||
      (!messages.length || (!isLiveChatActive && lastMessage.position === 'right')) ||
      (createdAt && moment(createdAt).isBefore(moment().subtract(1, 'hour'))) ||
      (nonAvatarComponents.includes(lastMessageType) && lastMessagePosition !== 'right')
    ) {
      setCheckRender(false);
      return true;
    }

    STORE.dispatch(
      updateEnvironment({
        activeQuestion: lastMessage,
        activeQuestionType: lastMessage.type,
      })
    );
    setCheckRender(false);

    return false;
  }, [messages, checkShouldRender, environment?.activeQuestionType]);

  useEffect(() => {
    /**
     * Questions will be rendered based on the following conditions:
     * 1. If the environment exists and the type of view set in environment.
     * 2. If the viewType is 'launcher-icon', it further checks if the launcher icon opened (referring props.shouldFetchData) and
     * flows.length is greater than 0 to initiate question rendering. Otherwise, it only checks if flows.length is greater than 0.
     */
    const isLauncherIcon = environment?.viewType === 'launcher-icon';
    const shouldIntiateRendering = isLauncherIcon ?
      (flows.length && props?.shouldFetchData)
      : flows.length;

    if (shouldIntiateRendering) {
      const shouldRender = shouldRenderMessage();
      if (shouldRender) {
        renderQuestion();
      }
    }
  }, [flows, shouldRenderMessage, props?.shouldFetchData, environment?.viewType]);

  /**
   * Date: 24/Jan/2025
   * Author: Amit Kumar
   * Summary: Function to determine avatar visibility in a chat interface
   * Description:
   * The `showAvatar` function calculates whether an avatar should be displayed for a given message.
   * It evaluates based on message type, position, and avatar placement configuration.
   *
   * Key Points:
   * - Avatars are only displayed on the left side of messages (`position === 'left'`).
   * - If multiple messages are sent, the avatar appears only for the one.
   * - Logic for avatar placement varies based on the `avatarPlacement` setting:
   *   1. **Top Placement**:
   *      - Display the avatar if it's the first message, the last message is from the user, or the last message is a system message.
   *      - Avoid showing avatars for message types listed in `nonAvatarComponents`.
   *   2. **Default Placement**:
   *      - Show the avatar for left-positioned messages that are not system messages.
   *      - Ensure avatars do not appear for messages in `nonAvatarComponents`.
   *      - Handle edge cases, such as error messages or when the message is the last one and not sent by the user.
   */
  const showAvatar = (index: number, message: any) => {

    if (message.position === 'right' || message.type === 'system') {
      return false;
    }

    const previousMessage = messages[index - 1];


    if (design.avatarPlacement === 'top') {
      return (
        !previousMessage ||
        previousMessage.position === 'right' ||
        previousMessage.type === 'system'
      ) || (
          !nonAvatarComponents.includes(previousMessage?.type.toLowerCase()) && !nonAvatarComponents.includes(message.type.toLowerCase())
        );

    }

    return message.position !== 'right' && message.type !== 'system' &&
      (
        !nonAvatarComponents.includes(message.type.toLowerCase()) ||
        (index === messages.length - 1 && message.messagedBy !== 'user') ||
        message.isErrorMessage
      )
  }

  /**
   * Author: Divyansh
   * Date: 2024-30-05
   * Summary: Method to adjust top margin of messages
   * Description: This method is used to get top margin of messages since same side (left-right/user-bot) subsequent messages have less gap between them
   */
  const checkSameSideMessage = (position: 'left' | 'right', previousIndex: number): 'mt-3' | 'mt-2' => {
    if (previousIndex === -1) {
      return 'mt-3';
    }

    return messages[previousIndex].position === position ? 'mt-2' : 'mt-3';
  }

  /**
   * Date: 24/Jan/2025
   * Author: Amit Kumar
   * Summary: Function to generate CSS class names for chat messages
   * Description:
   * The `getMessageClassName` function determines the appropriate CSS class names for a chat message based on its type, 
   * position, and avatar placement configuration.
   *
   * Key Points:
   * - For messages of type included in `mediaOptions` and positioned on the right (`position === 'right'`):
   *   - Add a `flex-col-reverse` class to adjust the layout.
   * - For messages with `avatarPlacement` set to "top":
   *   - Only apply the `userMessageClassName` to adjust the layout accordingly.
   * - For all other configurations:
   *   - Add the default `flex` class along with the `userMessageClassName` for proper message alignment.
   */
  const getMessageClassName = (message: { type: 'left' | 'right', position: string }) => {
    const userMessageClassName = (mediaOptions.includes(message.type) && message.position === 'right')
      ? 'flex-col-reverse'
      : '';
    if (design.avatarPlacement === 'top') {
      return `${userMessageClassName}`;
    }
    return `flex ${userMessageClassName}`;
  }

  /**
   * Date: 24/Jan/2025
   * Author: Amit Kumar
   * Summary: Function to generate CSS class names for document-type chat messages
   * Description:
   * The `getMessageDocumentClassNames` function calculates the CSS class names for chat messages specifically related to document messages, based on their type, position, and avatar placement configuration.
   *
   * Key Points:
   * - For `avatarPlacement` set to "top":
   *   - Add `flex` and `items-end` for alignment.
   *   - If the message is included in `mediaOptions` and positioned on the right (`position === 'right'`):
   *     - Add the `flex-col-reverse` class to adjust the layout for media options.
   * - For configurations without `avatarPlacement` as "top":
   * - Return an empty string as no specific classes are applied.
   *
   */
  const getMessageDocumentClassNames = (message: { type: 'left' | 'right', position: string }) => {
    if (design.avatarPlacement === 'top') {
      return `flex items-end ${(mediaOptions.includes(message.type) && message.position === 'right') && 'flex-col-reverse'}`;
    }
    return ``;
  }

  return <>
    <div id="chatArea" >
      {
        // ternary operator here is used because using && renders 0 until messages.length is available
        messages.length
          ? messages.map((message: any, index: number) => {
            const count = messages.length;
            return (
              <div key={`${message.mid} ${String(message.createdAt)}`} className="ele-chatblock">
                <div className="main-container">
                  <Element name={message?.id + ' ' + (message.label || '').slice(-10)}>
                    <div className={`message bp-fade-in items-end ${getMessageClassName(message)}`}>
                      {
                        showAvatar(index, message)
                          ? <BotIcon type={message.messageBy === 'agent' ? 'agent' : 'bot'}></BotIcon>
                          : <div style={{ width: message.type === 'system' ? '0' : '50px' }}></div>
                      }
                      {
                        (textCompOutput.includes(message.type.toLowerCase()) && message.position !== 'right') && <Text message={message}
                          topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {
                        message.type === 'button' && <SingleSelect message={message} currentIndex={index} count={count}
                          topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {
                        message.type === 'multi_select' && <MultiSelect message={message} currentIndex={index} count={count}
                          topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {
                        message.type === 'rating' && <Rating message={message} currentIndex={index} count={count}
                          topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {
                        message.type === 'range' && <Range message={message} currentIndex={index} count={count}
                          topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {
                        message.type === 'image' && <Image message={message} topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {
                        message.type === 'video' && <Video message={message} topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {
                        message.type === 'contact' && <Contact message={message} topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {
                        message.type === 'iframe' &&
                        <>{design.avatarPlacement === 'top' ?
                          <div className="flex"><IframeLabel {...message} topMargin={checkSameSideMessage(message.position, index - 1)} /> </div>
                          : <IframeLabel {...message} topMargin={checkSameSideMessage(message.position, index - 1)} />
                        }</>
                      }
                      {
                        message.type === 'system' && <SystemMessage {...message} topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {
                        message.type === 'document' &&
                        <>{design.avatarPlacement === 'top' ?
                          <div className={getMessageDocumentClassNames(message)}><Document message={message} topMargin={checkSameSideMessage(message.position, index - 1)} /> </div>
                          : <Document message={message} topMargin={checkSameSideMessage(message.position, index - 1)} />
                        }</>

                      }
                      {
                        message.type === 'audio' && <Audio message={message} topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                      {/* 
                          Author: Satyam Sharma
                          Date: 07 January 2025
                          Description: This component handles AI question rendering and interaction.  
                          The following logic has been introduced to manage AI-specific functionality,  
                          including token-based streaming for message updates and feedback integration,  
                          while maintaining segregation with standard text components. 
                      */}
                      {
                        message.type.toLowerCase() === 'ai' && <Ai message={message} topMargin={checkSameSideMessage(message.position, index - 1)} />
                      }
                    </div>
                  </Element>
                  {/* Component is rendered whenever live chat is triggered */}
                  {
                    liveChatComp.includes(message.type.toLowerCase()) && <PushNotifications message={message}></PushNotifications>
                  }

                  {
                    (((message.messagedBy === 'user' || message.position === 'right') && !mediaOptions.includes(message.type))) && (
                      <div className="flex-col">
                        <div className="ele-chat-message-container flex flex-row-reverse pr-0">
                          <div className={`ele-chat-message-content max-w-80 rounded-xl break-normal py-2 px-4 bg-primaryColor text-white break-words relative ${checkSameSideMessage(message.position, index - 1)}`} style={{ backgroundColor: design.themeColor }}
                            onMouseEnter={() => { setMessageActionIndex(String(index)) }} onMouseLeave={() => { setMessageActionIndex('') }}>
                            {(message?._parent ?
                              <ParentChatMessage
                                backgroundColor={adjustHexColor(design.themeColor, 10)}
                                parentMessage={message?._parent} messagePosition={message.position}
                              />
                              : null
                            )}
                            <p className={message?._parent ? 'ml-4 ele-chat-message-label' : 'ele-chat-message-label'}>{message.label}</p>
                            <MessageActions message={message} show={messageActionIndex === String(index)} />
                          </div>
                        </div>
                        {
                          message?.isTemp &&  <ListeningDots></ListeningDots>
                        }
                       
                      </div>
                    )
                  }
                </div>

                {/* Messaged by Component  */}
                {
                  (index === messages.length - 1) && !environment.typing &&
                  (message.messageBy === 'agent' || (!liveChatComp.includes(messages[messages.length - 1]?.type?.toLowerCase()))) && <MessagedBy message={message}></MessagedBy>
                }


                {/*  Component to skip or go back to previous question*/}
                {
                  (message.skip || message.back) && (index === messages.length - 1) && !nonAvatarComponents.includes(messages[messages.length - 1]?.type?.toLowerCase()) && <div className="flex flex-row items-end justify-end cursor-pointer">
                    {
                      message.back &&
                      <div className="ele-back-btn rounded-xl py-2 px-5 border-x border-y border-customBorder2" onClick={back}>Back</div>
                    }
                    {
                      message.skip &&
                      <div className="ele-skip-btn rounded-xl py-2 px-5 bg-primaryBgColor ml-2" onClick={skip}>Skip</div>
                    }

                  </div>
                }
              </div>
            )
          })
          : ''
      }

      {
        environment.iframeActive && environment.iframeActive.source && <Iframe {...environment.iframeActive} />
      }

      {
        (environment.typing || (environment?.activeQuestion?.type?.toLowerCase() === 'ai' && environment.isAiResponding)) && <Typing />
      }

      <div id='ele-306'  >
        {
          environment?.activeQuestion?.type?.toLowerCase() === 'ai' 
          && haveSuggestions() 
          && (environment?.activeInput !== 'voice') &&
          <AiSuggestion />
        }
      </div>

    </div >
  </>
}
